import { useState } from "react";
import { UtensilsCrossed } from "lucide-react";
import { absoluteUrl } from "../lib/media";

/**
 * SafeImage — resilient <img> wrapper.
 * - Resolves relative backend URLs correctly (no double /api).
 * - Shows a subtle skeleton while loading.
 * - Falls back to a clean icon block on error, keeping aspect ratio.
 * Never replaces the whole card by a coloured block: it only replaces the
 * visual area of the image so surrounding UI keeps working.
 */
export default function SafeImage({
  src,
  alt = "",
  className = "",
  fallbackClassName = "",
  fit = "cover", // "cover" | "contain"
  onClick,
  "data-testid": testId,
}) {
  const resolved = absoluteUrl(src);
  const [state, setState] = useState(resolved ? "loading" : "error");

  const showFallback = !resolved || state === "error";

  return (
    <div className={`relative overflow-hidden ${className}`} onClick={onClick} data-testid={testId}>
      {!showFallback && (
        <>
          {state === "loading" && (
            <div className="absolute inset-0 bg-[#E9EDF3] animate-pulse" aria-hidden />
          )}
          <img
            src={resolved}
            alt={alt}
            loading="lazy"
            decoding="async"
            onLoad={() => setState("loaded")}
            onError={() => setState("error")}
            className={`absolute inset-0 w-full h-full ${fit === "cover" ? "object-cover" : "object-contain"} transition-opacity duration-300 ${state === "loaded" ? "opacity-100" : "opacity-0"}`}
            style={{ pointerEvents: onClick ? "auto" : undefined }}
          />
        </>
      )}
      {showFallback && (
        <div className={`absolute inset-0 flex items-center justify-center bg-[#F4F5EF] text-[#CDD7E3] ${fallbackClassName}`} aria-hidden>
          <UtensilsCrossed className="w-8 h-8" />
        </div>
      )}
    </div>
  );
}
